// Grafiektype 'Verdeling' voor een matrixvraag: per stelling hoe vaak elk
// antwoord gekozen is. Twee weergaven:
//   - MatrixDistributionBarChart: één groep per antwoord, een staaf per stelling
//   - MatrixDistributionPies: een cirkel per stelling
//
// Nodig omdat de bestaande matrixgrafieken per stelling een gemiddelde tonen,
// en de grafiek voor tekstantwoorden de antwoorden zonder volgorde en zonder de
// niet-gekozen antwoorden toont. Updateverzoek Drenthe beweegt, 2026-10-02.
//
// De gegevens komen kant-en-klaar uit matrixDistribution.js (via useMatrixData);
// hier wordt alleen getekend.
//
// Onder elk meetmoment staat de cijfertabel van dát meetmoment (zichtbaar op
// het PDF-vel en in 'Kopiëren als afbeelding'). Eén tabel onder het hele blok
// stond op papier pagina's na de eerste grafiek (testronde 2026-10-28).

import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    Legend,
    LabelList,
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
} from 'recharts'
import AngledAxisTick from '../AngledAxisTick'
import AnalyticsEmptyState from '../../dashboard/AnalyticsEmptyState'
import { CHART_MARK_STROKE, CHART_MARK_STROKE_WIDTH, getSeriesColor, labelTextTone } from '../../../helpers/chartPalette'
import { toDistributionBarData, categoryColors } from '../../../helpers/dataHelpers/matrixDistribution'
import { distributionTable } from '../../../helpers/dataHelpers/chartDataTable'
import ChartDataTable from '../../graphData/ChartDataTable'

/** @typedef {import('../../../helpers/dataHelpers/matrixDistribution').MomentDistribution} MomentDistribution */

/**
 * Getal boven een staaf; een nul blijft leeg, net als in een handgemaakte grafiek.
 *
 * @param {number} value
 * @returns {string}
 */
const countLabel = (value) => (value ? String(value) : '')

/**
 * Legendatekst in de gewone tekstkleur. Recharts kleurt hem anders als het
 * vlak, en de neutrale schaalkleur ('Neutraal') is dan bijna wit op wit.
 *
 * @param {string} value
 * @returns {JSX.Element}
 */
const legendText = (value) => <span className="matrix-distribution-legend-text">{value}</span>

/**
 * Getal midden in een taartpunt. Een SVG-tekst krijgt zijn maat via een klasse
 * met !important: de globale regel `text { font-size: 30px }` wint anders.
 *
 * Op een donker vlak lichte tekst, anders donkere (labelTextTone).
 *
 * @param {{cx: number, cy: number, midAngle: number, innerRadius: number, outerRadius: number, value: number, fill?: string}} props
 * @returns {JSX.Element|null}
 */
const PieCountLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, value, fill }) => {
    if (!value) return null
    const radius = innerRadius + (outerRadius - innerRadius) * 0.6
    const x = cx + radius * Math.cos((-midAngle * Math.PI) / 180)
    const y = cy + radius * Math.sin((-midAngle * Math.PI) / 180)
    return (
        <text
            x={x}
            y={y}
            textAnchor="middle"
            dominantBaseline="central"
            className={`matrix-distribution-label matrix-distribution-label--${labelTextTone(fill)}`}
        >
            {value}
        </text>
    )
}

/**
 * @param {object} props
 * @param {MomentDistribution[]} props.distribution - Verdeling per meetmoment
 * @param {Object<string, string>} [props.rowColors] - Stelling (titel) → kleur, uit de kaart of de huisstijl
 */
export const MatrixDistributionBarChart = ({ distribution, rowColors }) => {
    if (!Array.isArray(distribution) || distribution.length === 0) return <AnalyticsEmptyState />
    const showMoment = distribution.length > 1

    return (
        <div className="matrix-distribution">
            {distribution.map(moment => (
                <div key={moment.momentPosition ?? 'onbekend'} className="matrix-distribution-moment">
                    {showMoment && <h4 className="matrix-distribution-moment-title">{moment.moment}</h4>}
                    <ResponsiveContainer width="100%" height={340}>
                        <BarChart data={toDistributionBarData(moment)} margin={{ top: 20, right: 20, left: 0, bottom: 5 }}>
                            <XAxis dataKey="name" tick={<AngledAxisTick />} interval={0} height={70} axisLine={false} tickLine={false} />
                            <YAxis allowDecimals={false} axisLine={false} tickLine={false} />
                            <Tooltip />
                            <Legend verticalAlign="bottom" formatter={legendText} />
                            {moment.rows.map((row, index) => (
                                <Bar
                                    key={row.key}
                                    dataKey={row.key}
                                    name={`${row.title} (n = ${row.total})`}
                                    fill={rowColors?.[row.title] || getSeriesColor(index)}
                                    stroke={CHART_MARK_STROKE}
                                    strokeWidth={CHART_MARK_STROKE_WIDTH}
                                >
                                    <LabelList dataKey={row.key} position="top" formatter={countLabel} className="matrix-distribution-label" />
                                </Bar>
                            ))}
                        </BarChart>
                    </ResponsiveContainer>
                    <ChartDataTable table={distributionTable([moment])} />
                </div>
            ))}
        </div>
    )
}

/**
 * @param {object} props
 * @param {MomentDistribution[]} props.distribution - Verdeling per meetmoment
 */
export const MatrixDistributionPies = ({ distribution }) => {
    if (!Array.isArray(distribution) || distribution.length === 0) return <AnalyticsEmptyState />
    const showMoment = distribution.length > 1

    return (
        <div className="matrix-distribution">
            {distribution.map(moment => {
                const colors = categoryColors(moment.categories)
                return (
                    <div key={moment.momentPosition ?? 'onbekend'} className="matrix-distribution-moment">
                        {showMoment && <h4 className="matrix-distribution-moment-title">{moment.moment}</h4>}
                        <div className="matrix-distribution-pies">
                            {moment.rows.map(row => {
                                const slices = moment.categories.map(category => ({
                                    key: category.key,
                                    name: category.label,
                                    value: row.counts[category.key] || 0,
                                }))
                                return (
                                    <div key={row.key} className="matrix-distribution-pie">
                                        <h4 className="matrix-distribution-pie-title">{row.title}</h4>
                                        <p className="chart-sample-size">n = {row.total}</p>
                                        <ResponsiveContainer width="100%" height={260}>
                                            <PieChart>
                                                <Pie
                                                    data={slices}
                                                    dataKey="value"
                                                    nameKey="name"
                                                    outerRadius={110}
                                                    labelLine={false}
                                                    label={PieCountLabel}
                                                    isAnimationActive={false}
                                                >
                                                    {slices.map(slice => (
                                                        <Cell
                                                            key={slice.key}
                                                            fill={colors[slice.key]}
                                                            stroke={CHART_MARK_STROKE}
                                                            strokeWidth={CHART_MARK_STROKE_WIDTH}
                                                        />
                                                    ))}
                                                </Pie>
                                                <Tooltip />
                                            </PieChart>
                                        </ResponsiveContainer>
                                    </div>
                                )
                            })}
                        </div>
                        {/* Eén legenda voor alle cirkels van dit meetmoment: ze delen dezelfde
                            antwoorden en kleuren. Per cirkel stond hetzelfde rijtje er twee
                            keer (testronde 2026-10-05). Het kleurvlak is een SVG met een
                            fill-attribuut, geen inline style. */}
                        <ul className="matrix-distribution-legend" aria-label="Legenda">
                            {moment.categories.map(category => (
                                <li key={category.key}>
                                    <svg className="matrix-distribution-legend-swatch" width="12" height="12" aria-hidden="true">
                                        <rect width="12" height="12" fill={colors[category.key]} stroke={CHART_MARK_STROKE} strokeWidth={CHART_MARK_STROKE_WIDTH} />
                                    </svg>
                                    <span className="matrix-distribution-legend-text">{category.label}</span>
                                </li>
                            ))}
                        </ul>
                        <ChartDataTable table={distributionTable([moment])} />
                    </div>
                )
            })}
        </div>
    )
}

export default MatrixDistributionBarChart
